import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../../services/api';
import { useCurrency } from '../CurrencyContext';
import { CURRENCIES } from '../../constants';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';

const REGION_COLORS = ['#6366f1', '#4f46e5', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b'];

const GeographicRevenueChart: React.FC = () => {
  const { currencyCode, currencySymbol } = useCurrency();
  const [range, setRange] = useState<DateRangeOption>('ytd');
  const [geoData, setGeoData] = useState<any[]>([]);
  const activeRate = CURRENCIES.find(c => c.code === currencyCode)?.rate || 1;

  useEffect(() => {
    let isMounted = true;
    const fetchGeo = async () => {
      try {
        const res = await api.analytics.getGeographic(range);
        if (isMounted && Array.isArray(res)) {
          setGeoData(res.map((item: any, idx: number) => ({
            ...item,
            color: item.color || REGION_COLORS[idx % REGION_COLORS.length],
            displayRevenue: Math.floor(Number(item.revenue || 0) * activeRate)
          })).sort((a, b) => b.displayRevenue - a.displayRevenue));
        }
      } catch (err) {
        console.error('Failed to load geographic revenue:', err);
      }
    };
    fetchGeo();
    return () => { isMounted = false; };
  }, [range, activeRate]);

  return (
    <div id="geographic-revenue-chart" className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm h-full">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-black text-slate-900 mb-1">Regional Revenue</h2>
          <p className="text-sm text-slate-500">Live geographic distribution of subscription revenues.</p>
        </div>
        <ReportDateSelector value={range} onChange={setRange} />
      </div>
      <div className="h-[280px]">
        {geoData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading regional data...</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={geoData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" hide />
              <YAxis 
                dataKey="region" 
                type="category" 
                axisLine={false} 
                tickLine={false} 
                tick={{fontSize: 11, fontWeight: 700, fill: '#64748b'}}
                width={100}
              />
              <Tooltip 
                cursor={{fill: 'transparent'}}
                contentStyle={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05)' }}
                formatter={(v: number) => [`${currencySymbol}${v.toLocaleString()}`, 'Revenue']}
              />
              <Bar dataKey="displayRevenue" radius={[0, 6, 6, 0]} barSize={24}>
                {geoData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default GeographicRevenueChart;
